import { useState } from "react";
import { useLeads } from "@/hooks/use-leads";
import { useAuth } from "@/hooks/use-auth";
import { updateLeadByAstrologer } from "@/api/leads";
import { successToast, errorToast } from "@/lib/toast";
import Header from "@/components/Header";
import {
  UserPlus, Search, Phone, Mail, Calendar, Clock,
  Loader2, ChevronRight, RefreshCw, Sparkles, Send,
  CheckCircle2, XCircle, AlertCircle, MessageSquare,
  TrendingUp, Flame, Snowflake, CircleSlash, HelpCircle,
  PhoneCall, FileText, Tag
} from "lucide-react";
import { cn } from "@/utils/cn";
import { format } from "date-fns";

/* ----------------------------------------------------------------
 * Visual maps
 * --------------------------------------------------------------*/

// The 7 Lead_status values our backend allows. Order matters — we use
// it for the status-dropdown enum so the funnel reads top-to-bottom.
const STATUS_OPTIONS = [
  "Not Contacted",
  "Attempted to Contact",
  "Contacted",
  "Contact in Future",
  "Converted Lead",
  "Lost Lead",
  "Junk Lead",
];

// Short labels for the status tile row. The previous heuristic split on
// the first space, which turned "Contact in Future" into "Contact" —
// indistinguishable from "Contacted" sitting next to it. Map each
// status to a deliberate short label instead.
const statusShortLabel = {
  "Not Contacted":         "Not",
  "Attempted to Contact":  "Attempted",
  "Contacted":             "Contacted",
  "Contact in Future":     "Future",
  "Converted Lead":        "Converted",
  "Lost Lead":             "Lost",
  "Junk Lead":             "Junk",
};

const statusColors = {
  "Not Contacted":         { bg: "bg-yellow-100",  text: "text-yellow-700",  dot: "bg-yellow-500" },
  "Attempted to Contact":  { bg: "bg-amber-100",   text: "text-amber-700",   dot: "bg-amber-500" },
  "Contacted":             { bg: "bg-blue-100",    text: "text-blue-700",    dot: "bg-blue-500" },
  "Contact in Future":     { bg: "bg-indigo-100",  text: "text-indigo-700",  dot: "bg-indigo-500" },
  "Converted Lead":        { bg: "bg-green-100",   text: "text-green-700",   dot: "bg-green-500" },
  "Lost Lead":             { bg: "bg-red-100",     text: "text-red-700",     dot: "bg-red-500" },
  "Junk Lead":             { bg: "bg-gray-200",    text: "text-gray-600",    dot: "bg-gray-500" },
  "Pending":               { bg: "bg-gray-100",    text: "text-gray-700",    dot: "bg-gray-400" },
};

const CLASSIFICATION_OPTIONS = ["Pending", "Hot", "Warm", "Cold", "Dead"];
const classificationVisuals = {
  Hot:     { Icon: Flame,       color: "text-red-500",    bg: "bg-red-50",    label: "Hot" },
  Warm:    { Icon: TrendingUp,  color: "text-orange-500", bg: "bg-orange-50", label: "Warm" },
  Cold:    { Icon: Snowflake,   color: "text-sky-500",    bg: "bg-sky-50",    label: "Cold" },
  Dead:    { Icon: CircleSlash, color: "text-gray-500",   bg: "bg-gray-100",  label: "Dead" },
  Pending: { Icon: HelpCircle,  color: "text-gray-500",   bg: "bg-gray-100",  label: "Pending" },
};

// leadType -> human label + tooltip. ad_import = cold (we cold-call them),
// instant/scheduled = warm (in-app), callback_request = latent (in-app).
const leadTypeMeta = {
  ad_import:              { label: "Ad",       full: "Imported from ad campaign",        cls: "bg-purple-50 text-purple-700 border-purple-200" },
  instant_call_attempt:   { label: "Instant",  full: "Tried an instant call with you",   cls: "bg-orange-50 text-orange-700 border-orange-200" },
  scheduled_call_attempt: { label: "Booked",   full: "Booked a scheduled call with you", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  callback_request:       { label: "Callback", full: "Requested a callback from you",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  manual_add:             { label: "Manual",   full: "Added manually by admin",          cls: "bg-gray-50 text-gray-700 border-gray-200" },
};

/* ----------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------*/

const extractDateTimeFromRemarks = (remarks) => {
  if (!remarks) return { dob: null, tob: null, pob: null, concern: null };
  try {
    const dobMatch = remarks.match(/Date of Birth:\s*([^\n]+)/);
    const tobMatch = remarks.match(/Time of Birth:\s*([^\n]+)/);
    const pobMatch = remarks.match(/Place of Birth:\s*([^\n]+)/);
    const concernMatch = remarks.match(/Concern:\s*([^\n]+)/);
    return {
      dob: dobMatch ? dobMatch[1].trim() : null,
      tob: tobMatch ? tobMatch[1].trim() : null,
      pob: pobMatch ? pobMatch[1].trim() : null,
      concern: concernMatch ? concernMatch[1].trim() : null,
    };
  } catch {
    return { dob: null, tob: null, pob: null, concern: null };
  }
};

// In-app reachable when the lead has a linked User account. Set by:
//   - createCall / bookAppointmentWithICS / instantMeeting (server-side)
//   - addLeadToAstrologer when caller was authed
//   - OTP signup backfill on verifyOTPAndLogin
const isInAppReachable = (lead) => Boolean(lead?.user);

/* ----------------------------------------------------------------
 * Lead card (list)
 * --------------------------------------------------------------*/

const LeadCard = ({ lead, onClick, isSelected }) => {
  const status = lead.Lead_status || "Pending";
  const colors = statusColors[status] || statusColors.Pending;
  const { dob } = extractDateTimeFromRemarks(lead.Remarks);
  const typeMeta = leadTypeMeta[lead.leadType] || leadTypeMeta.manual_add;
  const reachable = isInAppReachable(lead);

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left",
        isSelected
          ? "bg-orange-100 border border-orange-300"
          : "bg-gray-50 hover:bg-gray-100 border border-transparent"
      )}
    >
      <div className={cn("w-2 h-2 rounded-full flex-shrink-0 mt-2", colors.dot)} />

      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-medium">
          {lead.fullName?.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?"}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{lead.fullName || "Unknown"}</p>
          {reachable && (
            <span title="In-app reachable">
              <Sparkles className="h-3 w-3 text-orange-500 flex-shrink-0" />
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">
          {dob ? format(new Date(dob), "MMM d, yyyy") : "No DOB"}
          {lead.createdAt && (
            <span> · Added {format(new Date(lead.createdAt), "MMM d")}</span>
          )}
        </p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-medium", typeMeta.cls)} title={typeMeta.full}>
            {typeMeta.label}
          </span>
          <span className={cn("text-[10px] px-1.5 py-0.5 rounded", colors.bg, colors.text)}>
            {statusShortLabel[status] || status}
          </span>
        </div>
      </div>

      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-2" />
    </button>
  );
};

/* ----------------------------------------------------------------
 * Lead detail (right pane)
 * --------------------------------------------------------------*/

const LeadDetail = ({ lead, onClose, onUpdated }) => {
  const { astrologer } = useAuth();
  const status = lead.Lead_status || "Pending";
  const classification = lead.Lead_classification || "Pending";
  const { dob, tob, pob, concern } = extractDateTimeFromRemarks(lead.Remarks);
  const typeMeta = leadTypeMeta[lead.leadType] || leadTypeMeta.manual_add;
  const colors = statusColors[status] || statusColors.Pending;
  const reachable = isInAppReachable(lead);

  const [savingStatus, setSavingStatus] = useState(false);
  const [savingClass, setSavingClass] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const applyPatch = async (payload) => {
    const { status, data, error } = await updateLeadByAstrologer({
      leadId: lead._id,
      accessToken: astrologer.accessToken,
      payload,
    });
    if (status === 200 && data?.data) {
      successToast("Lead updated");
      onUpdated?.(data.data);
      return true;
    }
    errorToast(error || "Couldn't update lead");
    return false;
  };

  const handleStatusChange = async (e) => {
    const next = e.target.value;
    if (next === status) return;
    setSavingStatus(true);
    try {
      await applyPatch({ Lead_status: next });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleClassificationChange = async (next) => {
    if (next === classification) return;
    setSavingClass(true);
    try {
      await applyPatch({ Lead_classification: next });
    } finally {
      setSavingClass(false);
    }
  };

  const handleAppendNote = async () => {
    const trimmed = noteDraft.trim();
    if (!trimmed) return;
    setSavingNote(true);
    try {
      const ok = await applyPatch({ Remarks: trimmed });
      if (ok) setNoteDraft("");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="bg-white rounded-lg h-full flex flex-col shadow-sm">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-medium">
                {lead.fullName?.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?"}
              </span>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 truncate">{lead.fullName || "Unknown"}</h3>
                {reachable && <Sparkles className="h-4 w-4 text-orange-500 flex-shrink-0" title="In-app reachable" />}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-medium", typeMeta.cls)}>
                  {typeMeta.label}
                </span>
                <span className={cn("text-xs px-2 py-0.5 rounded-full", colors.bg, colors.text)}>
                  {status}
                </span>
                {lead.paidAt && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium" title={`First paid: ${format(new Date(lead.paidAt), "MMM d, yyyy")}`}>
                    Paid
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden flex-shrink-0">
            <ChevronRight className="h-5 w-5 rotate-180" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Quick actions — in-app reachable vs out-of-app */}
        <div className={cn(
          "rounded-lg p-3 flex items-start gap-3",
          reachable ? "bg-orange-50 border border-orange-200" : "bg-amber-50 border border-amber-200"
        )}>
          <div className="flex-shrink-0 mt-0.5">
            {reachable
              ? <PhoneCall className="h-5 w-5 text-orange-600" />
              : <AlertCircle className="h-5 w-5 text-amber-600" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {reachable ? "In-app reachable" : "Not on the app yet"}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              {reachable
                ? "This lead has an account. You'll be able to call them via the app once the call-back action is enabled."
                : "They haven't signed up yet. Once they install the app and verify their phone, this lead becomes reachable in-app."}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                disabled
                title={reachable
                  ? "Coming soon: nudge this user to start a call with you"
                  : "Lead must be in-app reachable"}
                className="text-xs px-3 py-1.5 rounded-md bg-orange-600 text-white font-medium opacity-50 cursor-not-allowed flex items-center gap-1.5"
              >
                <PhoneCall className="h-3.5 w-3.5" />
                Nudge to call (soon)
              </button>
              {!reachable && (
                <button
                  disabled
                  title="Coming soon: send the user a WhatsApp invite with an install link"
                  className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white font-medium opacity-50 cursor-not-allowed flex items-center gap-1.5"
                >
                  <Send className="h-3.5 w-3.5" />
                  Send app invite (soon)
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Birth details (if extracted from Remarks or concern submitted) */}
        {(dob || tob || pob || concern) && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Birth Details
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">Date:</span>
                <span className="text-gray-900">{dob || "Not provided"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">Time:</span>
                <span className="text-gray-900">{tob || "Not provided"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <UserPlus className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">Place:</span>
                <span className="text-gray-900">{pob || "Not provided"}</span>
              </div>
              {concern && (
                <div className="flex items-start gap-2 text-sm pt-1 border-t mt-2">
                  <MessageSquare className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-500">Concern:</span>
                  <span className="text-gray-900">{concern}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact (masked) */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Phone className="h-4 w-4 text-gray-400" />
            Contact
          </h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-500">Phone:</span>
              <span className="text-gray-900 font-mono">
                {lead.phone ? `${lead.phone.slice(0, 4)}\u2022\u2022\u2022\u2022${lead.phone.slice(-2)}` : "Not provided"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-500">Email:</span>
              <span className="text-gray-900">
                {lead.email ? `${lead.email.slice(0, 3)}\u2022\u2022\u2022@\u2022\u2022\u2022` : "Not provided"}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Contact details are partially hidden. All communication happens through the platform.
          </p>
        </div>

        {/* Classification (judgment) */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <Tag className="h-4 w-4 text-gray-400" />
              Classification
            </h4>
            {savingClass && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {CLASSIFICATION_OPTIONS.map((opt) => {
              const v = classificationVisuals[opt];
              const active = classification === opt;
              return (
                <button
                  key={opt}
                  onClick={() => handleClassificationChange(opt)}
                  disabled={savingClass}
                  className={cn(
                    "flex flex-col items-center gap-1 py-2 rounded-md border text-xs transition-colors",
                    active
                      ? "border-orange-500 bg-orange-50 ring-1 ring-orange-300"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  )}
                >
                  <v.Icon className={cn("h-4 w-4", active ? v.color : "text-gray-400")} />
                  <span className={cn(active ? "text-gray-900 font-medium" : "text-gray-600")}>{v.label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-2">Your judgment of how likely this lead is to convert.</p>
        </div>

        {/* Status (funnel stage) */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-gray-400" />
              Funnel stage
            </h4>
            {savingStatus && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
          </div>
          <select
            value={status}
            onChange={handleStatusChange}
            disabled={savingStatus}
            className="w-full text-sm border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-2">
            Auto-set by the system on real events (call &gt; 2min: Contacted, completed paid call: Converted). Override here for nuance.
          </p>
        </div>

        {/* Notes (append-only) */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-gray-400" />
            Add a note
          </h4>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="e.g. Spoke about her career concerns, follow up next Tuesday."
            rows={3}
            className="w-full text-sm border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleAppendNote}
              disabled={!noteDraft.trim() || savingNote}
              className="text-xs px-3 py-1.5 rounded-md bg-orange-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Append note
            </button>
          </div>
        </div>

        {/* Full remarks/timeline (read-only) */}
        {lead.Remarks && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Timeline</h4>
            <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans leading-relaxed">{lead.Remarks}</pre>
          </div>
        )}

        {/* Meta */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Meta</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Source</span>
              <span className="text-gray-900">{typeMeta.full}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Lead created</span>
              <span className="text-gray-900">
                {lead.createdAt ? format(new Date(lead.createdAt), "MMM d, yyyy 'at' h:mm a") : "-"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Last updated</span>
              <span className="text-gray-900">
                {lead.updatedAt ? format(new Date(lead.updatedAt), "MMM d, yyyy 'at' h:mm a") : "-"}
              </span>
            </div>
            {lead.paidAt && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">First paid call</span>
                <span className="text-emerald-700 font-medium">
                  {format(new Date(lead.paidAt), "MMM d, yyyy")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------
 * Page
 * --------------------------------------------------------------*/

export default function LeadsPage() {
  const { leads, loading, totalItems, getLeads, updateLeadLocally } = useLeads();
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const filteredLeads = (leads || [])
    .filter((lead) =>
      !searchTerm ||
      lead.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm)
    )
    .filter((lead) =>
      statusFilter === "all" || lead.Lead_status === statusFilter
    )
    .filter((lead) =>
      typeFilter === "all" || lead.leadType === typeFilter
    );

  const statusCounts = (leads || []).reduce((acc, lead) => {
    const s = lead.Lead_status || "Pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = (leads || []).reduce((acc, lead) => {
    const t = lead.leadType || "manual_add";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const handleSelected = (lead) => {
    setSelectedLead(lead);
  };

  const handleLeadUpdated = (updated) => {
    updateLeadLocally?.(updated);
    setSelectedLead(updated);
  };

  return (
    <>
      <Header name="Leads" />
      <div className="w-full p-4">
        {/* Status tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-3">
          <button
            onClick={() => setStatusFilter("all")}
            className={cn(
              "p-2 rounded-lg text-center transition-colors",
              statusFilter === "all" ? "bg-orange-500 text-white" : "bg-white hover:bg-gray-50"
            )}
          >
            <p className="text-lg font-bold">{totalItems || 0}</p>
            <p className="text-xs">All</p>
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn(
                "p-2 rounded-lg text-center transition-colors",
                statusFilter === s ? "bg-orange-500 text-white" : "bg-white hover:bg-gray-50"
              )}
              title={s}
            >
              <p className="text-lg font-bold">{statusCounts[s] || 0}</p>
              <p className="text-xs truncate">{statusShortLabel[s] || s}</p>
            </button>
          ))}
        </div>

        {/* leadType chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <button
            onClick={() => setTypeFilter("all")}
            className={cn(
              "text-xs px-2.5 py-1 rounded-full border transition-colors",
              typeFilter === "all"
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
            )}
          >
            All sources
          </button>
          {Object.entries(leadTypeMeta).map(([type, meta]) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={cn(
                "text-xs px-2.5 py-1 rounded-full border transition-colors",
                typeFilter === type
                  ? "bg-gray-900 text-white border-gray-900"
                  : `${meta.cls} hover:border-gray-400`
              )}
              title={meta.full}
            >
              {meta.label} {typeCounts[type] ? `· ${typeCounts[type]}` : ""}
            </button>
          ))}
        </div>

        {/* Two-pane layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-260px)]">
          {/* Lead list */}
          <div className="lg:col-span-1 bg-white rounded-lg flex flex-col shadow-sm">
            <div className="p-3 border-b flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <button
                onClick={getLeads}
                className="p-2 border rounded-lg hover:bg-gray-50"
                title="Refresh"
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <UserPlus className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No leads found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Leads land here when users try to call you, book a meeting, or come in from an ad campaign.
                  </p>
                </div>
              ) : (
                filteredLeads.map((lead) => (
                  <LeadCard
                    key={lead._id}
                    lead={lead}
                    onClick={() => handleSelected(lead)}
                    isSelected={selectedLead?._id === lead._id}
                  />
                ))
              )}
            </div>
          </div>

          {/* Detail pane */}
          <div className={cn(
            "lg:col-span-2",
            !selectedLead && "hidden lg:flex lg:items-center lg:justify-center lg:bg-white lg:rounded-lg lg:shadow-sm"
          )}>
            {selectedLead ? (
              <LeadDetail
                lead={selectedLead}
                onClose={() => setSelectedLead(null)}
                onUpdated={handleLeadUpdated}
              />
            ) : (
              <div className="text-center text-gray-500">
                <UserPlus className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Select a lead to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

